import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5 } from "styled-components";
import { AnimatePresence } from "framer-motion";
import dynamic from "next/dynamic";
import React from "react";
import { getThemeStyle } from "~/features/ui/get-theme-style";
import { InfoWindow } from "~/features/blocks/inventory/distributors/distributor-info-window";
import { type DistributorCardFragment } from "~/gql/graphql";
import { takeFirst } from "~/features/app/utils";
import { MapPlaceholder } from "~/features/ui/map/map-placeholder";
const Map = dynamic(() => import("~/features/ui/map/map").then(mod => mod.Map), {
  ssr: false
});
const hasGoogleMapsKey = Boolean(process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY);
const getActiveDistributor = (distributors: DistributorCardFragment[], coordinate?: google.maps.LatLng | undefined) => {
  return distributors.find(d => {
    return d?.locations?.find(l => {
      return l?.location?.lat === coordinate?.lat() && l?.location?.lng === coordinate?.lng();
    });
  });
};
type Props = {
  distributors: DistributorCardFragment[];
  activeDistributor: DistributorCardFragment | null;
  isZoomed: boolean;
  setIsZoomed: (v: boolean) => void;
  onDistributorClick: (distributor: DistributorCardFragment) => void;
  onDistributorHover?: (distributor: DistributorCardFragment) => void;
  activeCountry: string;
};
export const DistributorsMap = (props: Props) => {
  const {
    distributors,
    onDistributorClick,
    isZoomed,
    setIsZoomed,
    activeCountry,
    activeDistributor
  } = props;
  const themeStyle = getThemeStyle({
    mode: "light"
  });
  const [activeCoordinates, setActiveCoordinates] = React.useState<google.maps.LatLng[]>([]);
  const [hoveredMarker, setHoveredMarker] = React.useState<DistributorCardFragment | null>(null);
  const [hoveredCoordinate, setHoveredCoordinate] = React.useState<google.maps.LatLng | undefined>(undefined);
  const infoWindowRef = React.useRef<HTMLDivElement>(null);
  const animationFrameRef = React.useRef<number | null>(null);
  const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const isHoveringInfoWindowRef = React.useRef(false);
  const isHoveringMarkerRef = React.useRef(false);
  const onMarkerClick = (coordinate?: google.maps.LatLng) => {
    const hoveredCoords = coordinate;
    if (activeCoordinates.some(c => c.lat() === coordinate?.lat() && c.lng() === coordinate?.lng())) {
      setIsZoomed(false);
      setActiveCoordinates([]);
      return;
    }
    if (coordinate) {
      setIsZoomed(true);
      setActiveCoordinates([coordinate]);
    }
    const newActiveDistributor = getActiveDistributor(distributors, coordinate || hoveredCoords);
    if (newActiveDistributor) {
      onDistributorClick(newActiveDistributor);
    }
  };
  const onMarkerHover = (coordinate?: google.maps.LatLng) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    isHoveringMarkerRef.current = true;
    const newActiveDistributor = getActiveDistributor(distributors, coordinate);
    if (newActiveDistributor?.id) {
      setHoveredMarker(newActiveDistributor);
      setHoveredCoordinate(coordinate);
    }
  };
  const onMarkerHoverOut = () => {
    isHoveringMarkerRef.current = false;
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      if (!isHoveringMarkerRef.current && !isHoveringInfoWindowRef.current) {
        setHoveredMarker(null);
        setHoveredCoordinate(undefined);
      }
    }, 300);
  };
  const onInfoWindowMouseEnter = () => {
    isHoveringInfoWindowRef.current = true;
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
  };
  const onInfoWindowMouseLeave = () => {
    isHoveringInfoWindowRef.current = false;
    if (!isHoveringMarkerRef.current) {
      onMarkerHoverOut();
    }
  };
  const locations = React.useMemo(() => {
    return distributors.flatMap(distributor => (distributor?.locations || []).filter(l => l?.location?.lat != null && l?.location?.lng != null).map(l => ({
      lat: l?.location?.lat!,
      lng: l?.location?.lng!,
      distributorId: distributor.id
    })));
  }, [distributors]);
  React.useEffect(() => {
    if (activeDistributor && window.google?.maps) {
      setHoveredMarker(null);
      if (activeDistributor.locations.length === 1) {
        const coords = takeFirst(activeDistributor.locations)?.location;
        if (coords?.lat !== undefined && coords?.lng !== undefined) {
          setActiveCoordinates([new google.maps.LatLng(coords.lat, coords.lng)]);
        }
      } else {
        setActiveCoordinates(activeDistributor.locations.map(l => l.location).filter(l => l !== undefined && l.lat !== undefined && l.lng !== undefined).map(l => new google.maps.LatLng(l!.lat!, l!.lng!)));
      }
    }
  }, [activeDistributor]);
  React.useEffect(() => {
    const animate = () => {
      if (hoveredMarker && hoveredCoordinate && infoWindowRef.current) {
        const lat = hoveredCoordinate.lat();
        const lng = hoveredCoordinate.lng();
        const markerSelector = `[data-marker="${lat}-${lng}"]`;
        const markerEl = document.querySelector(markerSelector);
        if (markerEl) {
          const parentEl = markerEl.parentElement;
          const transformStyle = parentEl ? window.getComputedStyle(parentEl).transform : "";
          if (transformStyle && transformStyle !== "none") {
            const markerRect = markerEl.getBoundingClientRect();
            infoWindowRef.current.style.top = `${markerRect.top - markerRect.height * 1.2}px`;
            infoWindowRef.current.style.left = `${markerRect.left + markerRect.width / 2}px`;
            infoWindowRef.current.style.zIndex = "200";
          }
        }
      }
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    animationFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [hoveredMarker, hoveredCoordinate]);
  React.useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);
  return <_StyledDiv $_css={[{
    "position": "relative",
    "zIndex": "10"
  }, themeStyle.background, themeStyle.border]}>
      <_StyledDiv2>
        <_StyledDiv3 id="distributors-map">
          {hasGoogleMapsKey ? <Map centerOnChange={Boolean(activeCountry)} isZoomed={isZoomed} locations={locations} activeLocations={activeCoordinates} onMarkerClick={onMarkerClick} onMarkerHover={onMarkerHover} onMarkerHoverOut={onMarkerHoverOut} /> : <MapPlaceholder />}
          <_StyledDiv4>
            {distributors.length} {distributors.length === 1 ? "result" : "results"}
          </_StyledDiv4>

          <AnimatePresence>
            <_StyledDiv5>
              {hoveredMarker && <InfoWindow ref={infoWindowRef} {...hoveredMarker} onMouseEnter={onInfoWindowMouseEnter} onMouseLeave={onInfoWindowMouseLeave} />}
            </_StyledDiv5>
          </AnimatePresence>
        </_StyledDiv3>
      </_StyledDiv2>
    </_StyledDiv>;
};
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-v1y8a8-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-v1y8a8-1"
})({
  "overflow": "hidden",
  "borderRadius": "2px",
  "padding": "1.6rem",
  "@media (min-width: 1024px)": {
    "padding": "4rem"
  }
});
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-v1y8a8-2"
})({
  "position": "relative",
  "height": "40rem",
  "@media (min-width: 1024px)": {
    "height": "calc(100svh - 80px)"
  }
});
var _StyledDiv4 = _styled4("div").withConfig({
  componentId: "sc-sf-v1y8a8-3"
})({
  "position": "absolute",
  "right": "0.8rem",
  "top": "0.8rem",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(208 208 218 / var(--tw-bg-opacity))",
  "padding": "0.8rem",
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.2rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.024rem",
  "fontWeight": "400"
});
var _StyledDiv5 = /*#__PURE__*/_styled5("div").withConfig({
  displayName: "distributors-map___StyledDiv5",
  componentId: "sc-9t70vc-0"
})({
  "display": "none",
  "@media (min-width: 1024px)": {
    "display": "block"
  }
});